<template>
    <div
        class="canned-message-selector"
        :class="{
            'search-mode': isSearchStarted(),
        }"
    >
        <div class="empty" v-if="isEmpty()">
            <div class="title">{{ $t("message.noCannedMessages") }}</div>
            <div class="message">{{ $t("message.filterToFindMore") }}</div>
        </div>
        <template v-else>
            <div class="groups" v-if="!isSearchStarted()">
                <div
                    v-for="(group, index) in getItems()"
                    class="group"
                    :data-id="group._id"
                    :data-pointer="index"
                    :key="group._id"
                    :class="{
                        selected: selectedGroupId === group._id,
                        hoverSelected: pointerside === 'groups' && getSelectedGroupIndex() === index,
                    }"
                    @click="selectGroup(group._id)"
                >
                    <div class="title">
                        {{ getContentFromLanguage(group, "name") }}
                    </div>
                    <i class="material-icons"> chevron_right </i>
                </div>
            </div>
            <div class="items">
                <div
                    class="item-card"
                    v-for="(item, index) of getSelectedGroup().children"
                    :data-id="item._id"
                    :data-pointer="index"
                    :key="item._id"
                    @click="triggerReadyContent(item, 'content')"
                    :class="{
                        selected: pointerIndex === index,
                        hoverSelected: pointerside === 'items' && pointerIndex === index,
                    }"
                >
                    <div class="title-wrapper">
                        <div class="title">
                            <span v-if="isSearchStarted">
                                {{ getContentFromLanguage(item.parent, "name") }}
                                <i class="material-icons"> chevron_right </i>
                            </span>
                            {{ getContentFromLanguage(item, "name") }}
                        </div>
                        <div class="shortcut">
                            {{ getPressKey(item) }}
                        </div>
                    </div>
                    <div class="message-preview">
                        {{ getContentFromLanguage(item, "content") }}
                    </div>
                </div>
            </div>
        </template>
    </div>
</template>
<script>
import Vue from "vue";
import { mapState } from "vuex";
import { SanitizeHtml } from "../../../../../../utils/utils";
import SYSTEM_PUBSUB from "../../../../../../utils/systemPubSub";
import _ from "lodash";
import log2 from "../../../../../../utils/log2";
import Fuse from "fuse.js";

const log = log2("CannedMessageSelector");
export default Vue.extend({
    name: "CannedMessageSelector",
    data() {
        return {
            search: "",
            selectedGroupId: null,
            pointerside: "items",
            pointerIndex: 0,
            cancels: [],
        };
    },
    props: {
        defaultSearch: {
            type: String,
        },
        type: {
            type: String,
        },
        contact: {
            type: Object,
        },
    },
    computed: {
        ...mapState({
            readyContents: (state) => state.content.contents.data,
        }),
    },
    mounted() {
        const items = this.getItems();
        this.selectedGroupId = items[0]?._id;
        const keydownCallback = (e) => {
            console.log("CannedMessageSelector.keydown", e.key);
            if (e.key === "ArrowDown") {
                if (this.pointerside === "items") {
                    const index = Math.min(this.pointerIndex + 1, this.getSelectedGroup().children.length - 1);
                    const id = this.getSelectedGroup().children[index]?._id;
                    const el = document.querySelector(`div[data-id='${id}']`);
                    el?.scrollIntoView({
                        behavior: "smooth",
                    });
                    this.pointerIndex = index;
                } else {
                    const selectedGroupIndex = items.findIndex((item) => item._id === this.selectedGroupId);
                    this.selectedGroupId = items[selectedGroupIndex + 1]?._id || items[0]._id;
                    this.pointerIndex = 0;
                }
            } else if (e.key === "ArrowUp") {
                if (this.pointerside === "items") {
                    const index = Math.max(this.pointerIndex - 1, 0);
                    const id = this.getSelectedGroup().children[index]?._id;
                    const el = document.querySelector(`div[data-id='${id}']`);
                    el?.scrollIntoView({
                        behavior: "smooth",
                    });
                    this.pointerIndex = index;
                } else {
                    const selectedGroupIndex = items.findIndex((item) => item._id === this.selectedGroupId);
                    this.selectedGroupId = items[selectedGroupIndex - 1]?._id || items[items.length - 1]._id;
                    this.pointerIndex = 0;
                }
            }

            // left to pointer side = groups
            else if (e.key === "ArrowLeft") {
                if (this.isSearchStarted()) return;
                this.pointerside = "groups";
                const selectedGroupIndex = items.findIndex((item) => item._id === this.selectedGroupId);
                this.pointerIndex = selectedGroupIndex || 0;
            } else if (e.key === "ArrowRight") {
                if (this.pointerside !== "items") {
                    this.pointerside = "items";
                    this.pointerIndex = 0;
                } else {
                    this.pointerIndex = Math.min(this.pointerIndex + 1, this.getSelectedGroup().children.length - 1);
                }
            } else if (e.key === "Enter") {
                this.triggerReadyContent(this.getSelectedGroup().children[this.pointerIndex]);
            } else if (this.type === "canned-messages-tab") {
                const items = this.getSelectedGroup().children;
                const triggered = items.find((item) => item.number === e.key);
                if (triggered) {
                    this.triggerReadyContent(triggered);
                    e.preventDefault();
                }
            }
        };
        window.addEventListener("keydown", keydownCallback);
        this.cancels.push(() => window.removeEventListener("keydown", keydownCallback));
    },
    created() {
        this.search = this.defaultSearch || "";
    },
    methods: {
        getSelectedGroup() {
            const items = this.getItems();
            return (
                items.find((item) => item._id === this.selectedGroupId) || {
                    children: [],
                }
            );
        },
        getSelectedGroupIndex() {
            const items = this.getItems();
            return items.findIndex((item) => item._id === this.selectedGroupId);
        },
        isEmpty() {
            const items = Object.values(this.getItems())
                .map((x) => x.children)
                .flat();
            return items.length === 0;
        },
        getItems() {
            let allItems = this.readyContents;
            const parents = allItems
                .filter((readyContent) => !readyContent.parentId)
                .reduce((acc, readyContent) => {
                    acc[readyContent._id] = {
                        ...readyContent,
                        children: [],
                    };
                    return acc;
                }, {});
            let readyContents = allItems.reduce((acc, readyContent) => {
                if (acc[readyContent.parentId]) {
                    readyContent.parent = allItems.find((x) => x._id === readyContent.parentId);
                    acc[readyContent.parentId].children.push(readyContent);
                }
                return acc;
            }, parents);

            Object.keys(readyContents).forEach((key) => {
                readyContents[key].children = this.deepFilter(readyContents[key].children);
                if (readyContents[key].children.length === 0) {
                    delete readyContents[key];
                }
            });
            if (this.isSearchStarted()) {
                // collect all to one group
                const result = {};
                result["all"] = {
                    _id: "all",
                    name: "Arama Sonuçları",
                    children: Object.values(readyContents).reduce((acc, group) => {
                        return [...acc, ...group.children];
                    }, []),
                };
                readyContents = result;
                this.selectedGroupId = "all";
            }
            log("getItems", readyContents);

            return Object.values(readyContents);
        },
        isSearchStarted() {
            const q = (this.search || "").trim().toLowerCase().replace("#", "");
            return q !== "";
        },
        deepFilter(items) {
            const q = (this.search || "").trim().toLowerCase().replace("#", "");
            if (q === "") return items;
            items = items.map((x) => {
                x.nameCalculated =
                    this.type === "canned-messages-tab" ? x.letter : this.getContentFromLanguage(x, "name");
                x.contentCalculated = this.getContentFromLanguage(x, "content");
                x.hashCalculated = "#" + x.hash;
                return x;
            });
            const fuse = new Fuse(items, {
                includeScore: true,
                keys: ["nameCalculated", "contentCalculated", "hashCalculated"],
            });
            items = items
                .map((x) => {
                    const nameMatched = x.nameCalculated.toLowerCase().includes(q);
                    const contentMatched = x.contentCalculated.toLowerCase().includes(q);
                    const hashMatched = x.hashCalculated.toLowerCase().includes(q);
                    const nameStartsWith = x.nameCalculated.toLowerCase().startsWith(q);
                    const contentStartsWith = x.contentCalculated.toLowerCase().startsWith(q);
                    const hashStartsWith = x.hashCalculated.toLowerCase().startsWith(q);
                    // includes point = 50 startsWith point = 100
                    const namePoint = nameStartsWith ? 100 : nameMatched ? 50 : 0;
                    const contentPoint = contentStartsWith ? 75 : contentMatched ? 50 : 0;
                    const hashPoint = hashStartsWith ? 200 : hashMatched ? 50 : 0;
                    x.point = namePoint + contentPoint + hashPoint;
                    return x;
                })
                .filter((x) => x.point > 0);

            items = _.orderBy(items, ["point"], ["desc"]);

            const deepResults = fuse
                .search(q)
                .filter((x) => x.score < 0.25)
                .map((x) => x.item);

            const results = _.uniqBy([...items, ...deepResults], "_id");

            return results;
        },
        isValid(item) {
            const q = (this.search || "").trim().toLowerCase().replace("#", "");
            if (q === "") return true;

            const nameMatched = this.getContentFromLanguage(item, "name").toLowerCase().includes(q);
            const contentMatched = this.getContentFromLanguage(item, "content").toLowerCase().includes(q);
            console.log("isValid", {
                name: this.getContentFromLanguage(item, "name"),
                content: this.getContentFromLanguage(item, "content"),
                q,
                result: nameMatched || contentMatched,
            });
            return nameMatched || contentMatched;
        },
        getContentFromLanguage(item, key = "content", text = true) {
            const content =
                _.get(item, `languageContent.${this.$i18n.locale}.${key}`) ||
                _.get(item, `languageContent.${Object.keys(item.languageContent)[0]}.${key}`) ||
                "";
            return text ? SanitizeHtml(content, true) : content;
        },
        selectGroup(groupId) {
            this.selectedGroupId = groupId;
        },
        triggerReadyContent(item) {
            SYSTEM_PUBSUB.publish("ready_content_used", {
                content: this.getContentFromLanguage(item, "content", false),
                contact: this.contact,
            });
            this.$emit("selected", {
                type: "canned-messages",
                payload: item,
                contact: this.contact,
            });
        },
        getPressKey(item) {
            const keys = {
                ALT: `ALT + ${item.letter}`,
                HASH: `#${item.hash}`,
                TAB: `${item.letter} + TAB > ${item.number}`,
            };
            return keys[item.type];
        },
    },
    beforeDestroy() {
        log("beforeDestroy");
        this.cancels.forEach((cancel) => cancel());
    },
    watch: {
        defaultSearch: {
            handler(val) {
                this.search = val;
                this.selectedGroupId = this.getItems()[0]?._id;
            },
            immediate: true,
        },
        pointerIndex(val) {
            if (this.pointerside === "items") {
                const el = this.$el.querySelector(`.item-card[data-pointer="${val}"]`);
                if (el) {
                    el.scrollIntoView({ behavior: "smooth", block: "center" });
                }
            }
        },
        selectedGroupId(val) {
            const el = this.$el.querySelector(`.group[data-id="${val}"]`);
            if (el) {
                el.scrollIntoView({ behavior: "smooth", block: "center" });
            }
        },
    },
});
</script>

<style scoped lang="scss">
.canned-message-selector {
    width: 100%;
    display: flex;
    height: 100%;
    min-height: inherit;

    &.search-mode {
        .groups {
            display: none;
        }

        .items {
            width: 100%;
        }
    }

    .empty {
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        gap: 10px;
        height: 100%;
        width: 100%;

        .title {
            font-size: 16px;
            font-weight: 700;
            color: #6c757d;
        }

        .message {
            font-size: 14px;
            font-weight: 500;
            color: #6c757d;
        }
    }

    .groups {
        display: flex;
        flex-direction: column;
        width: 30%;
        background-color: #f1f4f7;
        overflow: auto;

        .group {
            display: flex;
            justify-content: space-between;
            padding: 5px 10px;
            cursor: pointer;

            &:hover {
                background-color: #f5f5f5;
            }

            &.hoverSelected {
                box-shadow: 2px 2px 2px 2px var(--color-bg-primary-hover);
            }

            .title {
                font-size: 14px;
                font-weight: 600;
                color: #6c757d;
            }

            .material-icons {
                visibility: hidden;
            }

            &.selected {
                background-color: #e9ecef;

                .title {
                    color: #000;
                }

                .material-icons {
                    visibility: visible;
                }
            }
        }
    }

    .items {
        display: flex;
        flex-direction: column;
        width: 70%;
        background-color: #fff;
        overflow: auto;

        .item-card {
            display: flex;
            flex-direction: column;
            gap: 3px;
            padding: 5px 5px;
            cursor: pointer;
            border-bottom: 1px solid #f1f4f7;

            &:hover,
            &.selected {
                background-color: #f5f5f5;
            }

            &.hoverSelected {
                box-shadow: 2px 2px 2px 2px var(--color-bg-primary-hover);
            }

            .title-wrapper {
                display: flex;
                justify-content: space-between;

                .title {
                    font-size: 14px;
                    font-weight: 700;
                    color: #6c757d;
                    display: flex;
                    align-items: center;

                    span {
                        display: flex;
                        align-items: center;
                    }
                }

                .shortcut {
                    font-size: 12px;
                    font-weight: 400;
                    color: #6c757d;
                }
            }

            .message-preview {
                font-size: 12px;
                font-weight: 500;
                color: #6c757d;
                -webkit-line-clamp: 2;
                overflow: hidden;
                display: -webkit-box;
                -webkit-box-orient: vertical;
                border-left: 3px solid #6c757d;
                padding-left: 3px;
            }
        }
    }
}
</style>
